import { formatDistanceToNowStrict } from 'date-fns';

import { type DriOwnerUpdate } from '@apps/web/components/admin/accounts/account_card';
import {
    ACCOUNT_HEALTH_STATUS_CONFIG,
    type AccountHealthStatus,
} from '@apps/web/components/admin/accounts/account_health_status_model';
import {
    type AccountOrderBy,
    type AccountOrderDirection,
} from '@apps/web/routes/_app/admin/accounts/account_display_settings';
import {
    type AccountFilterChip,
    cardMatchesFilterChips,
    isOwnerIsChip,
    kickoffWeekKey,
    kickoffWeekLabel,
} from '@apps/web/routes/_app/admin/accounts/account_filters';
import { type AccountBoardTab } from '@apps/web/routes/_app/admin/accounts/account_view_layout';
import {
    type AccountKanbanCardRead,
    type AccountKanbanColumnRead,
    type AccountOwnerCandidateRead,
    ApiError,
    type GetAdminAccountsKanbanResponse,
} from '@libraries/typescript/api_client';

/*
 * Board-agnostic data logic for the admin accounts page: grouping,
 * filtering, sorting, drag-target codecs, and error-message mapping.
 * Everything here is pure — the page component owns all state and
 * side effects.
 */

export const ACCOUNTS_TABS = [
    'board',
    'account_management',
    'all',
    'calendar',
    'needs_review',
    'cohorts',
    'health',
] as const;
export type AccountsTab = (typeof ACCOUNTS_TABS)[number];

export function sumContractedArr(
    cards: ReadonlyArray<AccountKanbanCardRead>,
): number | null {
    let total = 0;
    for (const card of cards) {
        if (card.excluded_from_arr_totals) {
            continue;
        }
        if (card.contracted_arr_cents === null) {
            return null;
        }
        total += card.contracted_arr_cents;
    }
    return total;
}

export type AccountsTableRow = {
    readonly card: AccountKanbanCardRead;
    readonly stageTitle: string;
    readonly boardIndex: number;
};
export type AccountsTableSection = {
    readonly group: AccountKanbanGroup;
    readonly rows: ReadonlyArray<AccountsTableRow>;
};
export type DriKanbanLane = {
    readonly kind: 'owner';
    readonly key: string;
    readonly name: string;
    readonly email: string | null;
    readonly initials: string;
    readonly cardCount: number;
    readonly columns: ReadonlyArray<AccountKanbanColumnRead>;
};
export type HealthKanbanLane = {
    readonly kind: 'health';
    readonly key: string;
    readonly name: string;
    readonly healthStatus: AccountHealthStatus;
    readonly cardCount: number;
    readonly columns: ReadonlyArray<AccountKanbanColumnRead>;
};
export type StageTableGroup = {
    readonly kind: 'stage';
    readonly key: string;
    readonly name: string;
    readonly cardCount: number;
    readonly columns: ReadonlyArray<AccountKanbanColumnRead>;
};
export type KickoffWeekKanbanGroup = {
    readonly kind: 'kickoff_week';
    readonly key: string;
    readonly name: string;
    readonly cardCount: number;
    readonly columns: ReadonlyArray<AccountKanbanColumnRead>;
};
export type AccountKanbanGroup =
    | DriKanbanLane
    | HealthKanbanLane
    | StageTableGroup
    | KickoffWeekKanbanGroup;
export type KanbanDroppableTarget = {
    readonly stage: string;
    readonly groupKey: string | null;
};
export type KanbanDragDecisionInput = {
    readonly sourceStage: string;
    readonly sourceDriLaneKey: string | null;
    readonly targetStage: string;
    readonly targetDriLaneKey: string | null;
    readonly targetOwnerEmail: string | null;
};
export type KanbanDragDecision = {
    readonly kind:
        | 'none'
        | 'stage_only'
        | 'owner_reassign'
        | 'owner_clear'
        | 'stage_and_owner_reassign'
        | 'stage_and_owner_clear';
    readonly targetStage: string;
    readonly ownerEmail: string | null;
};

export const UNASSIGNED_DRI_LANE_KEY = 'unassigned';
export const HEALTH_GROUP_ORDER = [
    'green',
    'yellow',
    'red',
    'neutral',
] as const satisfies ReadonlyArray<AccountHealthStatus>;
// Mirrors Core's TEST_ACCOUNT_TAG_SLUG.
const TEST_ACCOUNT_TAG_SLUG = 'test';
const GROUPED_DROPPABLE_ID_PREFIX = 'dri-lane:';
const UNGROUPED_DROPPABLE_ID_PREFIX = 'lane:';

// The "Proposal Sent" column is hidden from Deployment by default — those
// accounts aren't actively worked — and revealed via a display toggle.
export const PROPOSAL_SENT_STAGE = 'proposal_sent';
// Moving a card here cascades the org to the churned lifecycle stage, which
// disables EHR/voice/outreach systems, so the move is gated behind an explicit
// confirmation dialog.
export const CHURNED_STAGE = 'churned';
// Each of the two board tabs renders the columns Core tagged with its board
// group (core/src/accounts/kanban.ts). Stage ids and moves are
// board-agnostic, the split is presentation only.
export const KANBAN_BOARD_FOR_TAB = {
    board: 'deployment',
    account_management: 'account_management',
} as const satisfies Record<AccountBoardTab, AccountKanbanColumnRead['board']>;

/**
 * Returns the stable DRI key used for grouping and drag targets.
 *
 * Email wins when present. Legacy cards without an owner email fall back to
 * the trimmed owner name, and casing never creates a second lane.
 */
export function getDriLaneKey(card: AccountKanbanCardRead): string {
    const ownerEmail = card.owner_email?.trim();
    if (ownerEmail) {
        return ownerEmail.toLowerCase();
    }

    const ownerName = card.owner_name?.trim();
    return ownerName ? ownerName.toLowerCase() : UNASSIGNED_DRI_LANE_KEY;
}

/**
 * Splits already-filtered, already-sorted board columns into visible DRI
 * lanes while preserving the board's stage and per-column card order.
 */
export function groupKanbanColumnsByDri(
    columns: ReadonlyArray<AccountKanbanColumnRead>,
    ownerCandidates: ReadonlyArray<AccountOwnerCandidateRead>,
): Array<DriKanbanLane> {
    const candidatesByEmail = new Map(
        ownerCandidates.map((owner) => [
            owner.email.trim().toLowerCase(),
            owner,
        ]),
    );
    const candidatesByName = new Map(
        ownerCandidates.map((owner) => [
            owner.name.trim().toLowerCase(),
            owner,
        ]),
    );
    const laneOwners = new Map<
        string,
        Pick<DriKanbanLane, 'name' | 'email' | 'initials'>
    >();

    function derivedInitials(name: string, email: string | null): string {
        const source = name || email?.split('@')[0] || '';
        const parts = source
            .replace(/[._-]+/gu, ' ')
            .trim()
            .split(/\s+/u)
            .filter(Boolean);
        const firstPart = parts[0];
        if (!firstPart) {
            return '?';
        }
        if (parts.length === 1) {
            return firstPart.slice(0, 2).toUpperCase();
        }

        const lastPart = parts[parts.length - 1] ?? firstPart;
        return `${firstPart.charAt(0)}${lastPart.charAt(0)}`.toUpperCase();
    }

    for (const card of columns.flatMap((column) => column.cards)) {
        const laneKey = getDriLaneKey(card);
        if (laneOwners.has(laneKey)) {
            continue;
        }
        if (laneKey === UNASSIGNED_DRI_LANE_KEY) {
            laneOwners.set(laneKey, {
                name: 'Unassigned',
                email: null,
                initials: 'UN',
            });
            continue;
        }

        const ownerEmail = card.owner_email?.trim() || null;
        const ownerName = card.owner_name?.trim() || null;
        const candidate = ownerEmail
            ? candidatesByEmail.get(ownerEmail.toLowerCase())
            : ownerName
              ? candidatesByName.get(ownerName.toLowerCase())
              : undefined;
        const displayEmail = candidate?.email.trim() || ownerEmail;
        const displayName =
            candidate?.name.trim() || ownerName || displayEmail || 'Unassigned';
        laneOwners.set(laneKey, {
            name: displayName,
            email: displayEmail,
            initials:
                candidate?.initials.trim() ||
                derivedInitials(
                    candidate?.name.trim() || ownerName || '',
                    displayEmail,
                ),
        });
    }

    return [...laneOwners.entries()]
        .map(([key, owner]) => {
            const laneColumns = columns.map((column) => ({
                ...column,
                cards: column.cards.filter(
                    (card) => getDriLaneKey(card) === key,
                ),
            }));
            const cardCount = laneColumns.reduce(
                (total, column) => total + column.cards.length,
                0,
            );

            return {
                kind: 'owner' as const,
                key,
                ...owner,
                cardCount,
                columns: laneColumns,
            };
        })
        .sort((leftLane, rightLane) => {
            if (leftLane.key === UNASSIGNED_DRI_LANE_KEY) {
                return 1;
            }
            if (rightLane.key === UNASSIGNED_DRI_LANE_KEY) {
                return -1;
            }

            const nameComparison = leftLane.name.localeCompare(
                rightLane.name,
                undefined,
                { sensitivity: 'base' },
            );
            return nameComparison !== 0
                ? nameComparison
                : leftLane.key.localeCompare(rightLane.key);
        });
}

/** Splits board columns into the card health values shown by HealthStatusDot. */
export function groupKanbanColumnsByHealth(
    columns: ReadonlyArray<AccountKanbanColumnRead>,
): Array<HealthKanbanLane> {
    return HEALTH_GROUP_ORDER.flatMap((healthStatus) => {
        const groupColumns = columns.map((column) => ({
            ...column,
            cards: column.cards.filter(
                (card) => card.health_status === healthStatus,
            ),
        }));
        const cardCount = groupColumns.reduce(
            (total, column) => total + column.cards.length,
            0,
        );
        if (cardCount === 0) {
            return [];
        }

        return [
            {
                kind: 'health',
                key: `health:${healthStatus}`,
                name: ACCOUNT_HEALTH_STATUS_CONFIG[healthStatus].label,
                healthStatus,
                cardCount,
                columns: groupColumns,
            },
        ];
    });
}

/** Groups dated kickoffs by Monday-start week, with missing dates last. */
export function groupKanbanColumnsByKickoffWeek(
    columns: ReadonlyArray<AccountKanbanColumnRead>,
): Array<KickoffWeekKanbanGroup> {
    const weekKeys = new Set<string>();
    let hasMissingDate = false;
    for (const card of columns.flatMap((column) => column.cards)) {
        const weekKey = kickoffWeekKey(card.go_live_date);
        if (weekKey === null) {
            hasMissingDate = true;
        } else {
            weekKeys.add(weekKey);
        }
    }

    const orderedWeekKeys: Array<string | null> = [...weekKeys].sort();
    if (hasMissingDate) {
        orderedWeekKeys.push(null);
    }

    return orderedWeekKeys.map((weekKey) => {
        const groupColumns = columns.map((column) => ({
            ...column,
            cards: column.cards.filter(
                (card) => kickoffWeekKey(card.go_live_date) === weekKey,
            ),
        }));

        return {
            kind: 'kickoff_week',
            key: `kickoff_week:${weekKey ?? 'no_date'}`,
            name:
                weekKey === null
                    ? 'No kickoff date'
                    : kickoffWeekLabel(weekKey),
            cardCount: groupColumns.reduce(
                (total, column) => total + column.cards.length,
                0,
            ),
            columns: groupColumns,
        };
    });
}

export function filterKanbanColumns(
    columns: ReadonlyArray<AccountKanbanColumnRead>,
    filters: {
        readonly boardTab: AccountBoardTab | null;
        readonly searchQuery: string;
        readonly filterChips: Array<AccountFilterChip>;
        readonly showProposalSent: boolean;
        readonly showChurned: boolean;
        readonly showChurnedDropTarget: boolean;
        // The All Accounts view spans every stage except Proposal Sent
        // (still behind its toggle), and always hides internal accounts.
        readonly allAccounts: boolean;
        readonly arrRange: AccountArrRange;
    },
): ReadonlyArray<AccountKanbanColumnRead> {
    return columns
        .filter(
            (column) =>
                filters.boardTab === null ||
                column.board === KANBAN_BOARD_FOR_TAB[filters.boardTab] ||
                // Churned is useful as a recovery/diagnostic lane even while
                // working from Deployment, so its explicit toggle crosses
                // the otherwise strict board boundary.
                (filters.showChurned && column.stage === CHURNED_STAGE),
        )
        .filter(
            (column) =>
                filters.showProposalSent ||
                column.stage !== PROPOSAL_SENT_STAGE,
        )
        .filter(
            (column) =>
                filters.allAccounts ||
                filters.showChurned ||
                filters.showChurnedDropTarget ||
                column.stage !== CHURNED_STAGE,
        )
        .map((column) => ({
            ...column,
            cards: column.cards.filter(
                (card) =>
                    !(filters.allAccounts && isInternalAccount(card)) &&
                    cardMatchesArrRange(card, filters.arrRange) &&
                    cardMatchesFilters(card, filters),
            ),
        }));
}

export function sortKanbanColumns(
    columns: ReadonlyArray<AccountKanbanColumnRead>,
    orderBy: AccountOrderBy,
    orderDirection: AccountOrderDirection,
): ReadonlyArray<AccountKanbanColumnRead> {
    return columns.map((column) => ({
        ...column,
        cards: sortKanbanColumnCards(column.cards, orderBy, orderDirection),
    }));
}

export function sortKanbanColumnCards(
    cards: ReadonlyArray<AccountKanbanCardRead>,
    orderBy: AccountOrderBy,
    orderDirection: AccountOrderDirection,
): Array<AccountKanbanCardRead> {
    return [...cards]
        .map((card, boardIndex) => ({ card, boardIndex }))
        .sort((left, right) => {
            const comparison = compareAccountCards(
                left.card,
                right.card,
                orderBy,
                orderDirection,
            );
            if (comparison !== 0) {
                return comparison;
            }

            return left.boardIndex - right.boardIndex;
        })
        .map(({ card }) => card);
}

/** Internal accounts are Phoebe-internal orgs and test-tagged accounts. */
export function isInternalAccount(card: AccountKanbanCardRead): boolean {
    return (
        card.is_internal_phoebe ||
        card.tags.some((tag) => tag.slug === TEST_ACCOUNT_TAG_SLUG)
    );
}

/**
 * Inclusive contracted-ARR bounds in cents; null means unbounded. Accounts
 * without a known ARR never match an active bound.
 */
export type AccountArrRange = {
    readonly minCents: number | null;
    readonly maxCents: number | null;
};

export function cardMatchesArrRange(
    card: AccountKanbanCardRead,
    range: AccountArrRange,
): boolean {
    if (range.minCents === null && range.maxCents === null) {
        return true;
    }
    const arr = card.contracted_arr_cents;
    return (
        arr !== null &&
        (range.minCents === null || arr >= range.minCents) &&
        (range.maxCents === null || arr <= range.maxCents)
    );
}

function cardMatchesFilters(
    card: AccountKanbanCardRead,
    filters: {
        readonly searchQuery: string;
        readonly filterChips: Array<AccountFilterChip>;
    },
): boolean {
    if (!cardMatchesFilterChips(card, filters.filterChips)) {
        return false;
    }

    const query = filters.searchQuery.trim().toLowerCase();
    if (!query) {
        return true;
    }

    return accountSearchText(card).includes(query);
}

function accountSearchText(card: AccountKanbanCardRead): string {
    return [
        card.name,
        card.company_name,
        card.id,
        card.phoebe_organization_id,
        card.owner_name,
        card.owner_email,
        card.go_live_date,
        ...card.tags.flatMap((tag) => [tag.label, tag.slug]),
    ]
        .filter((value): value is string => Boolean(value))
        .join(' ')
        .toLowerCase();
}

/**
 * The per-user "Needs Review" queue: table rows whose review is due (Core's
 * `is_stale_in_stage` — an active deployment/nurture stage past the staleness
 * threshold without a fresh review), scoped to the signed-in admin by default.
 * An explicit owner filter chip (set via "My View" or the owner filter) already
 * narrowed `rows`, so its presence just suppresses the implicit self-scope,
 * letting an admin work another owner's queue.
 */
export function selectNeedsReviewRows(
    rows: ReadonlyArray<AccountsTableRow>,
    filterChips: ReadonlyArray<AccountFilterChip>,
    currentUserEmail: string | null,
): Array<AccountsTableRow> {
    const dueRows = rows.filter((row) => row.card.is_stale_in_stage);
    // Only an owner "is" chip is a positive scope that overrides the per-user
    // default; an "is not" exclusion chip must not (it would otherwise flip the
    // tab to the whole team's queue minus one owner).
    const hasOwnerScope = filterChips.some(isOwnerIsChip);
    const myEmail = currentUserEmail?.toLowerCase() ?? null;
    if (hasOwnerScope || myEmail === null) {
        return dueRows;
    }
    return dueRows.filter(
        (row) => row.card.owner_email?.toLowerCase() === myEmail,
    );
}

export function flattenKanbanColumns(
    columns: ReadonlyArray<AccountKanbanColumnRead>,
): ReadonlyArray<AccountsTableRow> {
    let boardIndex = 0;

    return columns.flatMap((column) =>
        column.cards.map((card) => {
            const row: AccountsTableRow = {
                card,
                stageTitle: column.title,
                boardIndex,
            };
            boardIndex += 1;
            return row;
        }),
    );
}

export function sortAccountsTableRows(
    rows: ReadonlyArray<AccountsTableRow>,
    orderBy: AccountOrderBy,
    orderDirection: AccountOrderDirection,
): Array<AccountsTableRow> {
    return [...rows].sort((leftRow, rightRow) => {
        const comparison = compareAccountCards(
            leftRow.card,
            rightRow.card,
            orderBy,
            orderDirection,
        );
        if (comparison !== 0) {
            return comparison;
        }

        return leftRow.boardIndex - rightRow.boardIndex;
    });
}

function compareAccountCards(
    leftCard: AccountKanbanCardRead,
    rightCard: AccountKanbanCardRead,
    orderBy: AccountOrderBy,
    orderDirection: AccountOrderDirection,
): number {
    let comparison: number;
    switch (orderBy) {
        case 'name':
            comparison = compareNullableText(leftCard.name, rightCard.name);
            break;
        case 'review_recency':
            comparison = compareNullableTimestamp(
                leftCard.last_reviewed_at,
                rightCard.last_reviewed_at,
                'first',
            );
            break;
        case 'time_in_stage': {
            return compareNullableTimestampsNullLast(
                leftCard.kanban_last_moved_at,
                rightCard.kanban_last_moved_at,
                orderDirection,
                (leftMs, rightMs) => rightMs - leftMs,
            );
        }
        case 'go_live_date': {
            return compareNullableTimestampsNullLast(
                leftCard.go_live_date,
                rightCard.go_live_date,
                orderDirection,
                (leftMs, rightMs) => leftMs - rightMs,
            );
        }
        case 'health_severity':
            comparison =
                ACCOUNT_HEALTH_STATUS_CONFIG[leftCard.health_status].sortRank -
                ACCOUNT_HEALTH_STATUS_CONFIG[rightCard.health_status].sortRank;
            break;
        default:
            return orderBy satisfies never;
    }

    return orderDirection === 'asc' ? comparison : -comparison;
}

function compareNullableTimestampsNullLast(
    left: string | null | undefined,
    right: string | null | undefined,
    direction: AccountOrderDirection,
    compareAscending: (leftMs: number, rightMs: number) => number,
): number {
    const leftMs = getSortableTimestamp(left);
    const rightMs = getSortableTimestamp(right);
    if (leftMs === null && rightMs === null) {
        return 0;
    }
    if (leftMs === null) {
        return 1;
    }
    if (rightMs === null) {
        return -1;
    }

    const comparison = compareAscending(leftMs, rightMs);
    return direction === 'asc' ? comparison : -comparison;
}

function compareNullableText(
    left: string | null | undefined,
    right: string | null | undefined,
): number {
    if (!left && !right) {
        return 0;
    }
    if (!left) {
        return 1;
    }
    if (!right) {
        return -1;
    }

    return left.localeCompare(right, undefined, { sensitivity: 'base' });
}

function compareNullableTimestamp(
    left: string | null | undefined,
    right: string | null | undefined,
    nullPlacement: 'first' | 'last',
): number {
    const leftMs = getSortableTimestamp(left);
    const rightMs = getSortableTimestamp(right);

    if (leftMs === null && rightMs === null) {
        return 0;
    }
    if (leftMs === null) {
        return nullPlacement === 'first' ? -1 : 1;
    }
    if (rightMs === null) {
        return nullPlacement === 'first' ? 1 : -1;
    }

    return leftMs - rightMs;
}

function getSortableTimestamp(value: string | null | undefined): number | null {
    if (!value) {
        return null;
    }

    const timestampMs = Date.parse(value);
    return Number.isNaN(timestampMs) ? null : timestampMs;
}

export function formatRelativeTimestamp(
    value: string | null | undefined,
): string | null {
    const timestampMs = getSortableTimestamp(value);
    if (timestampMs === null) {
        return null;
    }

    return formatDistanceToNowStrict(new Date(timestampMs), {
        addSuffix: true,
    });
}

/**
 * Encodes a stage droppable. Omitting the group key preserves the legacy
 * `lane:${stage}` identifier used by the ungrouped board.
 */
export function encodeKanbanDroppableId(
    stage: string,
    groupKey: string | null = null,
): string {
    if (groupKey === null) {
        return `${UNGROUPED_DROPPABLE_ID_PREFIX}${stage}`;
    }

    return (
        GROUPED_DROPPABLE_ID_PREFIX +
        `${encodeURIComponent(stage)}:${encodeURIComponent(groupKey)}`
    );
}

/** Decodes both grouped composite IDs and legacy ungrouped stage IDs. */
export function decodeKanbanDroppableId(
    id: unknown,
): KanbanDroppableTarget | null {
    if (typeof id !== 'string') {
        return null;
    }
    if (id.startsWith(UNGROUPED_DROPPABLE_ID_PREFIX)) {
        const stage = id.slice(UNGROUPED_DROPPABLE_ID_PREFIX.length);
        return stage ? { stage, groupKey: null } : null;
    }
    if (!id.startsWith(GROUPED_DROPPABLE_ID_PREFIX)) {
        return null;
    }

    const encodedTarget = id.slice(GROUPED_DROPPABLE_ID_PREFIX.length);
    const separatorIndex = encodedTarget.indexOf(':');
    if (separatorIndex <= 0 || separatorIndex === encodedTarget.length - 1) {
        return null;
    }

    try {
        const stage = decodeURIComponent(
            encodedTarget.slice(0, separatorIndex),
        );
        const groupKey = decodeURIComponent(
            encodedTarget.slice(separatorIndex + 1),
        );
        return stage && groupKey ? { stage, groupKey } : null;
    } catch {
        return null;
    }
}

/**
 * Classifies a drop independently from UI and network side effects.
 * A null target DRI key means the ungrouped board, not the Unassigned lane.
 */
export function getKanbanDragDecision(
    input: KanbanDragDecisionInput,
): KanbanDragDecision {
    const stageChanged = input.sourceStage !== input.targetStage;
    const driLaneChanged =
        input.targetDriLaneKey !== null &&
        input.sourceDriLaneKey !== input.targetDriLaneKey;
    const clearsOwner =
        driLaneChanged && input.targetDriLaneKey === UNASSIGNED_DRI_LANE_KEY;
    const reassignsOwner =
        driLaneChanged && !clearsOwner && input.targetOwnerEmail !== null;

    let kind: KanbanDragDecision['kind'] = 'none';
    if (stageChanged && clearsOwner) {
        kind = 'stage_and_owner_clear';
    } else if (stageChanged && reassignsOwner) {
        kind = 'stage_and_owner_reassign';
    } else if (stageChanged) {
        kind = 'stage_only';
    } else if (clearsOwner) {
        kind = 'owner_clear';
    } else if (reassignsOwner) {
        kind = 'owner_reassign';
    }

    return {
        kind,
        targetStage: input.targetStage,
        ownerEmail: reassignsOwner ? input.targetOwnerEmail : null,
    };
}

export function columnForStage(
    columns: ReadonlyArray<AccountKanbanColumnRead>,
    stage: string,
): AccountKanbanColumnRead | null {
    return columns.find((column) => column.stage === stage) ?? null;
}

export function cardForId(
    columns: ReadonlyArray<AccountKanbanColumnRead>,
    cardId: string | null,
): AccountKanbanCardRead | null {
    if (!cardId) {
        return null;
    }

    return (
        columns
            .flatMap((column) => column.cards)
            .find((card) => card.id === cardId) ?? null
    );
}

export function stringIdentifier(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
}

export function eventHasOptionKey(event: Event): boolean {
    return (
        (event instanceof MouseEvent || event instanceof KeyboardEvent) &&
        event.altKey
    );
}

export function boardWithMovedCard(
    board: GetAdminAccountsKanbanResponse,
    accountId: string,
    targetStage: string,
    source: 'drag_drop' | 'force_move',
    ownerUpdate: DriOwnerUpdate | null,
): GetAdminAccountsKanbanResponse {
    const movedAt = new Date().toISOString();
    const movedCard = board.columns
        .flatMap((column) => column.cards)
        .find((card) => card.id === accountId);

    if (!movedCard) {
        return board;
    }

    const stageChanged = movedCard.kanban_stage !== targetStage;
    const updatedCard: AccountKanbanCardRead = {
        ...movedCard,
        ...(stageChanged
            ? {
                  kanban_stage: targetStage,
                  kanban_stage_source: source,
                  kanban_last_moved_at: movedAt,
              }
            : {}),
        ...(ownerUpdate
            ? {
                  owner_email: ownerUpdate.email,
                  owner_name: ownerUpdate.name,
              }
            : {}),
    };

    if (!stageChanged) {
        return {
            ...board,
            fetched_at: movedAt,
            columns: board.columns.map((column) => ({
                ...column,
                cards: column.cards.map((card) =>
                    card.id === accountId ? updatedCard : card,
                ),
            })),
        };
    }

    const columnsWithoutCard = board.columns.map((column) => {
        const cards = column.cards.filter((card) => card.id !== accountId);

        return cards.length === column.cards.length
            ? column
            : { ...column, cards };
    });

    return {
        ...board,
        fetched_at: movedAt,
        columns: columnsWithoutCard.map((column) =>
            column.stage === targetStage
                ? { ...column, cards: [updatedCard, ...column.cards] }
                : column,
        ),
    };
}

export function stageMoveErrorMessage(error: unknown): string {
    return error instanceof Error
        ? error.message
        : 'Account stage did not save.';
}

export function ownerUpdateErrorMessage(error: unknown): string {
    return error instanceof Error
        ? error.message
        : 'Account owner did not save.';
}

export function reviewActionErrorMessage(error: unknown): string {
    return error instanceof Error
        ? error.message
        : 'Failed to mark account reviewed.';
}

// Core rejects guarded stage moves (owner/go-live guardrail, lifecycle-owned
// columns) with a 409 whose message names the admin force move escape hatch.
// Only those failures get the force-retry dialog; other errors stay toasts.
export function forceMoveOfferMessage(error: unknown): string | null {
    if (
        error instanceof ApiError &&
        error.status === 409 &&
        /force[\s-]?move/iu.test(error.message)
    ) {
        return error.message;
    }

    return null;
}
